import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useNotification } from "providers/NotificationProvider";
import { Link } from "react-router-dom";
import {
  Button,
  Card,
  CardBody,
  FormGroup,
  Form,
  Input,
  InputGroupAddon,
  InputGroupText,
  InputGroup,
  Container,
  Row,
  Col,
} from "reactstrap";
import AuthHeader from "components/Headers/AuthHeader.js";
import userServices from "services/userServices";
import AuthNavbar from "components/Navbars/AuthNavbar";
import { useTranslation } from "context/TranslationProvider";

function ResetPassword() {
  const navigate = useNavigate();
  const location = useLocation();
  const { notify } = useNotification();
  const { t } = useTranslation();
  const queryParams = new URLSearchParams(location.search);
  const token = queryParams.get("token");
  const email = queryParams.get("email");

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!password || !confirmPassword) {
      setMessage({ type: "error", text: t("toast.fillAllFields", "Please fill in all fields.") });
      return;
    }

    if (password !== confirmPassword) {
      setMessage({ type: "error", text: t("toast.passwordMismatch", "Passwords do not match") });
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await userServices.resetPassword({ email, token, password, confirmPassword });
      setIsSubmitting(false);
      if (response?.success) {
        notify("success", t(response.messageKey, response.message || t("auth.reset-password.success", "Password reset successfully")));
        navigate("/login");
      } else {
        const errorKey = response?.messageKey || response?.error;
        setMessage({ type: "error", text: t(errorKey, response?.message || t("toast.resetFailed", "Reset failed.")) });
      }
    } catch (error) {
      setIsSubmitting(false);
      console.error(error);
      const backendError = error?.response?.data?.messageKey || error?.response?.data?.error;
      const backendMessage = error?.response?.data?.message;
      setMessage({ type: "error", text: t(backendError, backendMessage || t("toast.unexpectedError", "Unexpected error occurred.")) });
    }
  };

  return (
    <>
      <AuthNavbar
        logo={{
          innerLink: "/",
          imgSrc: require("assets/img/images/logo.png"),
          imgAlt: "...",
        }}
      />
      <AuthHeader title={t("auth.reset-password.title", "Reset Password")} lead={t("auth.reset-password.lead", "Enter your new password below.")} />
      <Container className="mt--8 pb-5 px-3 px-md-4">
        <Row className="justify-content-center">
          <Col lg="5" md="7" xs="12">
            <Card className="bg-secondary border-0 mb-0 shadow">
              <CardBody className="px-lg-5 py-lg-5 px-4 py-4">
                <Form onSubmit={handleSubmit}>
                  <FormGroup className="mb-3">
                    <InputGroup className="input-group-merge input-group-alternative">
                      <InputGroupAddon addonType="prepend">
                        <InputGroupText>
                          <i className="ni ni-lock-circle-open" />
                        </InputGroupText>
                      </InputGroupAddon>
                      <Input
                        placeholder={t("auth.reset-password.newPassword", "New Password")}
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                      />
                    </InputGroup>
                  </FormGroup>

                  <FormGroup className="mb-3">
                    <InputGroup className="input-group-merge input-group-alternative">
                      <InputGroupAddon addonType="prepend">
                        <InputGroupText>
                          <i className="ni ni-lock-circle-open" />
                        </InputGroupText>
                      </InputGroupAddon>
                      <Input
                        placeholder={t("auth.reset-password.confirmPassword", "Confirm New Password")}
                        type="password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        required
                      />
                    </InputGroup>
                  </FormGroup>

                  <div className="text-center">
                    <Button color="info" type="submit" disabled={isSubmitting}>
                      {isSubmitting ? t("auth.reset-password.resetting", "Resetting...") : t("auth.reset-password.submitButton", "Reset Password")}
                    </Button>
                  </div>

                  {message.text && (
                    <div
                      className={`mt-3 text-center ${
                        message.type === "success" ? "text-success" : "text-danger"
                      }`}
                    >
                      {message.text}
                    </div>
                  )}
                </Form>
              </CardBody>
            </Card>
            <Row className="mt-3">
              <Col xs="12" className="text-center text-md-left">
                <Link to="/login">
                  <small className="font-weight-bold">{t("button.backToLogin", "Back to Login")}</small>
                </Link>
              </Col>
            </Row>
          </Col>
        </Row>
      </Container>
    </>
  );
}

export default ResetPassword;
